import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionTemplate, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskLines } from "@/components/site/Reveal";

const RotatingBadge = () => (
  <div className="absolute -left-10 bottom-10 z-10 hidden h-32 w-32 lg:block" aria-hidden="true">
    <svg viewBox="0 0 120 120" className="h-full w-full animate-spin-slow">
      <defs><path id="c" d="M60,60 m-46,0 a46,46 0 1,1 92,0 a46,46 0 1,1 -92,0" /></defs>
      <text className="fill-sand font-mono text-[10.5px] uppercase tracking-[0.3em]"><textPath href="#c">Charity · Training · Community ·</textPath></text>
    </svg>
    <span className="absolute inset-0 m-auto flex h-12 w-12 items-center justify-center rounded-full bg-ochre text-sand"><ArrowDownRight className="h-5 w-5" /></span>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 90, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 90, damping: 18 });
  const sx = useTransform(mx, (v) => `${v * 100}%`);
  const sy = useTransform(my, (v) => `${v * 100}%`);
  const spotlight = useMotionTemplate`radial-gradient(600px circle at ${sx} ${sy}, rgba(217,138,60,0.18), transparent 60%)`;

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="home-hero" className="relative min-h-[100svh] overflow-hidden bg-ink text-sand grain">
      <motion.div className="pointer-events-none absolute inset-0" style={{ background: spotlight }} />
      <div className="relative mx-auto grid max-w-[1440px] gap-12 px-5 pb-16 pt-32 sm:px-10 lg:grid-cols-12 lg:pt-40">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} className="eyebrow text-amber">
            Kamay · Gweagal Country · Dharawal Nation
          </motion.p>
          <MaskLines
            lines={["Strong in", <span key="c" className="italic text-ochre">culture.</span>, "Skilled for", "tomorrow."]}
            className="mt-8 font-display text-[3.6rem] font-medium leading-[0.9] tracking-tight sm:text-8xl sm:leading-[0.9] lg:text-[7.5rem] lg:leading-[0.9]"
          />
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }} className="mt-10 max-w-xl">
            <p className="text-base leading-relaxed text-sand/85 md:text-lg">
              GAC — Gweagal Aboriginal Corporation — is a community charity offering training, mentoring and cultural programmes
              that help our people grow, connect and lead.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/programmes" data-testid="hero-explore-button" className="btn-ochre group">
                Explore programmes <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
              </Link>
              <Link to="/donate" data-testid="hero-donate-button" className="btn-line border-sand/40 text-sand hover:border-sand hover:bg-sand hover:text-ink">
                Support our work
              </Link>
            </div>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5" style={{ perspective: 1200 }}>
          <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
            <motion.div
              initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
              animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
              transition={{ duration: 1.6, delay: 0.3, ease: [0.76, 0, 0.24, 1] }}
              className="relative h-[62vh] overflow-hidden rounded-t-[999px] lg:h-[74vh]"
            >
              <motion.img style={{ y: imgY, scale: imgScale }} src="/img/sandstone.jpg" alt="Sandstone cliffs meeting turquoise sea on the coast" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
            </motion.div>
            <div style={{ transform: "translateZ(60px)" }} className="absolute right-3 top-10 rounded-full bg-sand px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-ink shadow-2xl sm:right-6">
              Est. 2017
            </div>
          </motion.div>
          <RotatingBadge />
        </div>
      </div>
    </section>
  );
};
